/* eslint-disable */
import {
  AudioPopupDefinition,
  AUDIO_POPUP,
  IMAGE_MAP2,
  VIDEO_POPUP,
  VideoPopupDefinition,
  VIDEO,
  VideoDefinition,
  ImageMap2Definition
} from '../items';
import { Translator } from '../Translator';
import { translate } from './translate';
import { t } from 'ttag';
import { isIOS, isSafari, isMobile } from 'react-device-detect';

import {
  INTERACTIVE4_BTN_SIZE as BTN_SIZE,
  resolveInteractive4Overlaps
} from './interactive4Overlap';

/**
 * Same percentage basis as Interactive4 `getCoordinatesInPercentage`: pixel coords on the
 * source image → % of width/height (used for overlap math and layout).
 */
const getCoordinatesInPercentage = (
  leftPx: number,
  topPx: number,
  maxWidth: number,
  maxHeight: number
) => ({
  left: (leftPx * 100) / maxWidth,
  top: (topPx * 100) / maxHeight
});

const clampMobile80 = (left: number, top: number) => {
  let l = left;
  let t = top;
  if (isMobile) {
    if (l > 80) l = 80;
    if (t > 80) t = 80;
  }
  return { left: l, top: t };
};

export const Interactive3: Translator = sequence => {
  let imageDimensions: { width: number; height: number; color?: string };

  return translate(sequence, item => {
    if (item.__typename === 'ImageMap') {
      const newItemPicture = sequence.items?.find(
        i => i.__typename === 'Background'
      );

      imageDimensions = {
        width:
          (item as any).pictures?.[0]?.width ??
          (newItemPicture as any)?.pictures?.[0]?.width ??
          1024,
        height:
          (item as any).pictures?.[0]?.height ??
          (newItemPicture as any)?.pictures?.[0]?.height ??
          1024,
        color: (newItemPicture as any)?.pictures?.[0]?.color || ''
      };

      /** Intrinsic size for % coords + overlap safe-distance (matches ImageMap2 editor nudge). */
      const originMaxWidth = Math.max(1, imageDimensions.width);
      const originMaxHeight = Math.max(1, imageDimensions.height);

      return {
        type: IMAGE_MAP2,
        id: item.id,
        values: {
          imageUrl:
            (item as any).pictures?.[0]?.mediaUrl ||
            (newItemPicture as any)?.pictures?.[0]?.mediaUrl,
          body: t`Click the icons to continue.`,
          isFullScreen: true,
          color: imageDimensions.color || ''
        },
        items: (() => {
          const rawItems = item.items || [];

          const positions = rawItems.map(i => {
            const initialParts = (i.coordinates || '').split(',');
            const xPx = initialParts[0] ? Number(initialParts[0]) : 0;
            const yPx = initialParts[1] ? Number(initialParts[1]) : 0;
            const basePct = getCoordinatesInPercentage(
              Number.isFinite(xPx) ? xPx : 0,
              Number.isFinite(yPx) ? yPx : 0,
              originMaxWidth,
              originMaxHeight
            );
            const parts: string[] = [
              basePct.left.toString(),
              basePct.top.toString()
            ];

            // Handle dynamically added popup items (our new audio points)
            if (
              ((i as any).type === 'POPUP' ||
                (i as any).type === 'AudioPopup') &&
              ((i.coordinates && i.coordinates !== 'null') ||
                ((i as any).values?.left !== undefined &&
                  (i as any).values?.left !== null) ||
                ((i as any).values?.top !== undefined &&
                  (i as any).values?.top !== null))
            ) {
              let leftPercent = 0;
              let topPercent = 0;
              const coordinates =
                i?.coordinates || (i as any)?.settings?.coordinates || '';
              if ((i as any).type === 'AudioPopup' && (i as any).values) {
                leftPercent = parseFloat((i as any).values.left) || 0;
                topPercent = parseFloat((i as any).values.top) || 0;
              } else {
                const coordParts = coordinates.split(',');
                const xCoord = coordParts[0] ? Number(coordParts[0]) : 0;
                const yCoord = coordParts[1] ? Number(coordParts[1]) : 0;
                const c = getCoordinatesInPercentage(
                  Number.isFinite(xCoord) ? xCoord : 0,
                  Number.isFinite(yCoord) ? yCoord : 0,
                  originMaxWidth,
                  originMaxHeight
                );
                leftPercent = c.left;
                topPercent = c.top;
              }
              const finiteL = Number.isFinite(leftPercent) ? leftPercent : 0;
              const finiteT = Number.isFinite(topPercent) ? topPercent : 0;
              return clampMobile80(finiteL, finiteT);
            }

            const leftFromParts = parts[0] ? parseFloat(parts[0]) : 0;
            const topFromParts = parts[1] ? parseFloat(parts[1]) : 0;
            const finiteL = Number.isFinite(leftFromParts) ? leftFromParts : 0;
            const finiteT = Number.isFinite(topFromParts) ? topFromParts : 0;
            return clampMobile80(finiteL, finiteT);
          });

          if (positions.length > 1) {
            resolveInteractive4Overlaps(
              positions,
              isMobile,
              originMaxWidth,
              originMaxHeight
            );
          }

          return rawItems.map((i, index) => {
            const initialParts = (i.coordinates || '').split(',');
            const xPx2 = initialParts[0] ? Number(initialParts[0]) : 0;
            const yPx2 = initialParts[1] ? Number(initialParts[1]) : 0;
            const basePct2 = getCoordinatesInPercentage(
              Number.isFinite(xPx2) ? xPx2 : 0,
              Number.isFinite(yPx2) ? yPx2 : 0,
              originMaxWidth,
              originMaxHeight
            );
            const parts: string[] = [
              basePct2.left.toString(),
              basePct2.top.toString()
            ];

            const resolvedLeft = positions[index].left;
            const resolvedTop = positions[index].top;

            // Handle dynamically added popup items (our new audio points)
            // Process any POPUP item that has coordinates (regardless of audio)
            if (
              ((i as any).type === 'POPUP' ||
                (i as any).type === 'AudioPopup') &&
              ((i.coordinates && i.coordinates !== 'null') ||
                ((i as any).values?.left !== undefined &&
                  (i as any).values?.left !== null) ||
                ((i as any).values?.top !== undefined &&
                  (i as any).values?.top !== null))
            ) {
              const coordinates =
                i?.coordinates || (i as any)?.settings?.coordinates || '';

              const audioPopupData = {
                type: AUDIO_POPUP,
                id: (i as any).id || `${item.id}-${index}`, // Use real subitem ID if available
                items: [],
                values: {
                  // Match original behavior: show number only when variant is 'number'
                  title:
                    item.variant === 'number'
                      ? i.title || (i as any).values?.title || index + 1
                      : null,
                  left: resolvedLeft,
                  top: resolvedTop,
                  name: i.title || (i as any).values?.name || '',
                  body: i.body || (i as any).values?.body || '',
                  mediaUrl:
                    i.audios?.[0]?.mediaUrl ||
                    (i as any).values?.mediaUrl ||
                    '',
                  subtitlesUrl:
                    i.audios?.[0]?.subtitlesUrl ||
                    (i as any).values?.subtitlesUrl ||
                    '',
                  blocking: true,
                  replay: true,
                  isPopupCentered: true,
                  scroll: false,
                  pixelCoordinates: coordinates,
                  btnSize: BTN_SIZE
                }
              } as AudioPopupDefinition;

              return audioPopupData;
            } else if (i.videos && i.videos.length > 0) {
              const firstVideo = i.videos[0];
              return {
                type: VIDEO_POPUP,
                id: `${item.id}-${index}`,
                items: [
                  {
                    type: VIDEO,
                    id: `${item.id}-video`,
                    values: {
                      videoId: firstVideo?.id,
                      videoContainerStyles: {
                        width: '80%',
                        height: '70%',
                        borderRadius: '20px'
                      },
                      blocking: true,
                      isolated: false,
                      showPlayButton: true,
                      autoPlay: true,
                      mediaUrl:
                        isIOS || isSafari
                          ? ([
                              firstVideo?.mediaUrl && {
                                src: firstVideo.mediaUrl,
                                type: 'video/mp4'
                              }
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ].filter(Boolean) as any)
                          : ([
                              firstVideo?.hlsUrl && {
                                src: firstVideo.hlsUrl,
                                type: 'application/vnd.apple.mpegurl'
                              },
                              firstVideo?.mediaUrl && {
                                src: firstVideo.mediaUrl,
                                type: 'video/mp4'
                              }
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ].filter(Boolean) as any),
                      subtitlesUrl: firstVideo?.subtitlesUrl || '',
                      snapshotUrl: firstVideo?.snapshotUrl || ''
                    }
                  } as VideoDefinition
                ],
                values: {
                  title: item.variant == 'number' ? index + 1 : null,
                  left: resolvedLeft,
                  top: resolvedTop,
                  mediaUrl: i.videos?.[0]?.mediaUrl,
                  subtitlesUrl: i.videos?.[0]?.subtitlesUrl,
                  snapshotUrl: i.videos?.[0]?.snapshotUrl,
                  blocking: true,
                  replay: true
                }
              } as VideoPopupDefinition;
            } else {
              let titleForPopup = `${index + 1}`;

              if (initialParts[2]) {
                titleForPopup = initialParts[2];
              }

              return {
                type: AUDIO_POPUP,
                id: (i as any).id || `${item.id}-${index}`, // Use real subitem ID if available
                items: [],
                values: {
                  title: item.variant == 'number' ? titleForPopup : null,
                  left: resolvedLeft,
                  top: resolvedTop,
                  name: i.title,
                  body: i.body,
                  mediaUrl: i.audios?.[0]?.mediaUrl,
                  subtitlesUrl: i.audios?.[0]?.subtitlesUrl,
                  blocking: true,
                  replay: true,
                  isPopupCentered: true,
                  scroll: true,
                  pixelCoordinates:
                    i?.coordinates || (i as any)?.settings?.coordinates || '',
                  btnSize: BTN_SIZE
                }
              } as AudioPopupDefinition;
            }
          });
        })()
      } as ImageMap2Definition;
    }
    return null;
  });
};
